import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, catchError, combineLatest, filter, forkJoin, of, switchMap, takeUntil } from 'rxjs';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { UserFullInfoDto } from '@shared/models/user-full-info.model';
import { OrganizationLevel, OrganizationUnit, UnitEvent } from '../models/unit.model';
import { UnitService } from '../services/unit.service';
import { UnitHeaderComponent } from '../components/unit-header.component';

@Component({
  selector: 'app-unit-profile-shell-page',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, MatIconModule, MatProgressSpinnerModule, UnitHeaderComponent],
  template: `
    <div class="unit-profile-shell">
      <div *ngIf="isLoading" class="loading-container"><mat-spinner diameter="40"></mat-spinner></div>
      <div *ngIf="errorMessage && !isLoading" class="error-container" role="alert">
        <mat-icon color="warn">error</mat-icon><p>{{ errorMessage }}</p>
      </div>
      <div *ngIf="!isLoading && !errorMessage && unit" class="unit-profile-layout">
        <aside class="unit-profile-sidebar">
          <app-unit-header class="profile-header" [unit]="unit" [events]="events"></app-unit-header>
        </aside>
        <main class="unit-profile-content">
          <nav class="unit-profile-tabs" aria-label="Secciones de la unidad">
            <a *ngFor="let tab of tabs" [routerLink]="tab.path ? ['/units', unit.id, tab.path] : ['/units', unit.id]"
              routerLinkActive="active" [routerLinkActiveOptions]="{ exact: !tab.path }">{{ tab.label }}</a>
          </nav>
          <div class="unit-profile-route"><router-outlet></router-outlet></div>
        </main>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; height: 100%; min-height: 0; overflow: hidden; }
    .unit-profile-shell { height: 100%; min-height: 0; box-sizing: border-box; padding: clamp(12px, 2dvh, 24px); overflow: hidden; background: #EBF9FF; }
    .unit-profile-layout { display: grid; grid-template-columns: 300px minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); align-items: stretch; gap: 16px; width: 100%; height: 100%; min-height: 0; }
    .unit-profile-sidebar { box-sizing: border-box; min-width: 0; min-height: 0; overflow-y: auto; padding: 12px; border-radius: 24px; background: #fff; }
    .unit-profile-sidebar ::ng-deep .unit-header-container .header-content { padding: 0; border: 0; }
    .unit-profile-sidebar ::ng-deep .unit-header-container .name-row { flex-direction: column; align-items: center; text-align: center; }
    .unit-profile-sidebar ::ng-deep .unit-header-container .header-photo { width: 88px; height: 88px; }
    .unit-profile-sidebar ::ng-deep .unit-header-container .unit-name { white-space: normal; overflow-wrap: anywhere; }
    .unit-profile-sidebar ::ng-deep .unit-header-container .hierarchy-subtitle { flex-wrap: wrap; }
    .unit-profile-sidebar ::ng-deep .unit-header-container .unit-header-container { box-shadow: none; }
    .unit-profile-content { display: flex; flex-direction: column; box-sizing: border-box; width: 100%; height: 100%; min-width: 0; min-height: 0; overflow: hidden; padding: 0 16px 16px; border-radius: 24px; background: #fff; box-shadow: 0 4px 18px rgba(30, 70, 100, .08); }
    .unit-profile-content:has(app-unit-members-page) { padding-bottom: 0; }
    .unit-profile-tabs { display: flex; flex: 0 0 auto; gap: 0; overflow-x: auto; padding: 0 16px; border-bottom: 1px solid #e5e7eb; font-family: 'Inter', 'Helvetica Neue', sans-serif; }
    .unit-profile-tabs a { display: inline-flex; flex: 0 0 auto; align-items: center; min-height: 48px; padding: 0 12px; border-bottom: 2px solid transparent; color: #616161; font-size: 14px; font-weight: 400; line-height: 20px; text-decoration: none; white-space: nowrap; }
    .unit-profile-tabs a.active { color: #1675e8; border-bottom-color: #1675e8; font-weight: 600; }
    .unit-profile-route { flex: 1 1 auto; min-width: 0; min-height: 0; overflow: auto; }
    .unit-profile-route ::ng-deep > router-outlet + * { box-sizing: border-box; width: 100%; max-width: 1440px; margin-inline: auto; }
    .unit-profile-route ::ng-deep .agenda-page { margin-inline: auto; }
    .unit-profile-route:has(app-unit-members-page) { display: flex; flex-direction: column; overflow: hidden; }
    .unit-profile-route:has(app-unit-members-page) app-unit-members-page { flex: 1 1 auto; min-width: 0; min-height: 0; }
    .unit-profile-route ::ng-deep .feature-page { height: 100%; min-height: 0; padding: 16px; background: transparent; }
    .loading-container { display: flex; justify-content: center; align-items: center; min-height: 240px; }
    .error-container { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 240px; color: #d32f2f; }
    .error-container p { margin: 0; }
    @media (max-width: 1200px) { .unit-profile-layout { grid-template-columns: 260px minmax(0, 1fr); gap: 16px; } }
    @media (min-width: 901px) {
      .unit-profile-layout { grid-template-columns: 260px minmax(0, 1fr); }
      .unit-profile-sidebar { align-self: start; max-height: 100%; padding: 20px; box-shadow: 0 4px 18px rgba(30, 70, 100, .06); }
    }
    @media (max-width: 900px) {
      .unit-profile-shell { padding: 12px; }
      .unit-profile-layout { grid-template-columns: minmax(0, 1fr); grid-template-rows: auto minmax(0, 1fr); gap: 12px; }
      .unit-profile-sidebar { max-height: none; overflow: visible; padding: 14px; border-radius: 20px; }
      .unit-profile-sidebar ::ng-deep .unit-header-container .name-row { flex-direction: row; align-items: center; text-align: left; gap: 12px; }
      .unit-profile-sidebar ::ng-deep .unit-header-container .header-photo { width: 56px; height: 56px; }
      .unit-profile-sidebar ::ng-deep .unit-header-container .unit-name { font-size: 18px; line-height: 1.3; }
      .unit-profile-sidebar ::ng-deep .unit-header-container .hierarchy-subtitle { font-size: 12px; line-height: 1.5; }
      .unit-profile-content { min-height: 0; padding: 0 12px 12px; border-radius: 20px; }
      .unit-profile-tabs { padding: 0; }
    }
    @media (max-width: 600px) {
      .unit-profile-layout { gap: 8px; }
      .unit-profile-sidebar { padding: 6px 10px; border-radius: 16px; }
    }
  `],
})
export class UnitProfileShellPageComponent implements OnInit, OnDestroy {
  readonly communityLevel = OrganizationLevel.Comunidad;
  unit: OrganizationUnit | null = null;
  events: UnitEvent[] = [];
  isLoading = true;
  errorMessage: string | null = null;
  tabs: { path: string; label: string }[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly unitService: UnitService,
    private readonly userInfo: UserFullInfoService,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => this.redirectToVisibleTab());

    combineLatest([
      this.route.parent?.paramMap ?? this.route.paramMap,
      this.userInfo.getUserInfo$().pipe(filter((user): user is UserFullInfoDto => user !== null)),
    ]).pipe(
      switchMap(([params, user]) => {
        const unitId = params.get('unit-id');
        if (!unitId) {
          this.errorMessage = 'No se pudo abrir la unidad solicitada.';
          this.isLoading = false;
          return of(null);
        }
        this.isLoading = true;
        this.errorMessage = null;
        return this.unitService.getUnit(unitId).pipe(switchMap(unit => forkJoin({
          unit: of(unit),
          user: of(user),
          events: unit.organizationLevelId === this.communityLevel
            ? this.unitService.getEvents(unitId).pipe(catchError(() => of([] as UnitEvent[])))
            : of([] as UnitEvent[]),
        })));
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: result => {
        if (!result) return;
        this.unit = result.unit;
        this.updateTabs(result.user);
        this.events = result.events;
        this.breadcrumbService.setDynamicCrumb('unit-id', result.unit.name);
        this.isLoading = false;
        this.redirectToVisibleTab();
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'No se pudo cargar la unidad.';
        this.cdr.markForCheck();
      },
    });
  }

  private updateTabs(user: UserFullInfoDto): void {
    const canRead = this.userInfo.canViewRestrictedDataSnapshot(user) &&
      (user.isAdmin || user.readableUnitIds.some(id => id.toLowerCase() === this.unit!.id.toLowerCase()));
    this.tabs = [
      ...(canRead ? [
        { path: '', label: 'Inicio' },
        { path: 'agenda', label: 'Agenda' },
        { path: 'summary', label: 'Atención espiritual' },
        ...(this.userInfo.hasPermission('ManageSpiritualAssistanceVisits', user)
          ? [{ path: 'spiritual-assistance-visits', label: 'VAE' }] : []),
        { path: 'latest-divine-service', label: 'Último SD' },
        { path: 'divine-services', label: 'Servicios Divinos' },
        { path: 'birthdays', label: 'Cumpleaños' },
        { path: 'members', label: 'Miembros' },
        { path: 'ministry-bearers', label: 'Portadores de Ministerio' },
      ] : []),
      this.unit!.organizationLevelId === this.communityLevel
        ? { path: 'info', label: 'Datos de la comunidad' }
        : { path: 'communities', label: 'Comunidades' },
    ];
  }

  private redirectToVisibleTab(): void {
    if (this.isLoading || !this.unit || !this.tabs.length) return;
    const path = this.route.snapshot.firstChild?.routeConfig?.path ?? '';
    if (path === 'parent-history') return;
    if (!this.tabs.some(tab => tab.path === path)) {
      const firstPath = this.tabs[0].path;
      void this.router.navigate(firstPath ? ['/units', this.unit.id, firstPath] : ['/units', this.unit.id], { replaceUrl: true });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
